import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpUtilService } from "../http-util.service";

@Injectable({
  providedIn: "root",
})
export class StockHoldReleaseService {
  private readonly adminBaseUrl: string = "AdminService.asmx";
  private readonly modelSpecBaseUrl: string =
    "ModelSpecificationService.asmx";
  private readonly stockHoldBaseUrl: string = "StockHoldService.asmx";
  private readonly coloursBaseUrl: string =
    "ColoursByModelService.asmx";

  constructor(private http: HttpUtilService) {}

  /**
   * Fetches all admin records.
   * @returns An Observable with the API response.
   */
  getAll(): Observable<any> {
    return this.http.get(`${this.adminBaseUrl}/GetAll`);
  }

  /**
   * Fetches all active models in sales.
   * @returns An Observable with the API response.
   */
  getActiveModelsInSales(): Observable<any> {
    return this.http.get(
      `${this.modelSpecBaseUrl}/GetModelActiveInSalesDrpList`,
    );
  }

  /**
   * Fetches vehicle details based on the case number.
   * @param caseNumber The case number in the format `"ZDAE005"`.
   * @returns An Observable with the API response.
   */
  getVehicleByCaseNumber(caseNumber: string): Observable<any> {
    const params = `caseNumber=${encodeURIComponent(JSON.stringify(caseNumber))}`;
    return this.http.get(
      `${this.stockHoldBaseUrl}/GetVehicle?${params}`,
    );
  }

  /**
   * Updates stock hold information for a specific vehicle.
   * @param payload The request body containing stock hold details.
   * @returns An Observable with the API response.
   */
  updateStockHold(payload: {
    model: {
      __type: string;
      CaseNumber: string;
      EngineNumber: string | null;
      ChasisNumber: string | null;
      Model: string | null;
      Colour: string | null;
      DutyPaid: string;
      StockStatus: string;
      HoldDate: string;
      SalesConsultant: string | null;
      BuyerName: string | null;
      BuyerNric: string | null;
      ContractNo: string | null;
      RefNo: string | null;
    };
  }): Observable<any> {
    return this.http.post(`${this.stockHoldBaseUrl}/Update`, payload);
  }

  /**
   * Fetches stock hold report based on the case number.
   * @param caseNumber The case number in the format `"ZDAE005"`.
   * @returns An Observable with the API response.
   */
  getStockHoldReport(caseNumber: string): Observable<any> {
    const params = `model=${encodeURIComponent(JSON.stringify({ CaseNumber: caseNumber }))}`;
    return this.http.get(
      `${this.stockHoldBaseUrl}/GetStockHoldReport?${params}`,
    );
  }

  /**
   * Fetches colour dropdown list by model short description.
   * @param modelShortDesc The model short description, formatted as `"AVANTE 1.6 DCT HYBRID \"S\""`
   * @returns An Observable with the API response.
   */
  getColourDrpListByModel(modelShortDesc: string): Observable<any> {
    const params = `modelShortDesc=${encodeURIComponent(JSON.stringify(modelShortDesc))}`;
    return this.http.get(
      `${this.coloursBaseUrl}/GetColourDrpListByModelShortDesc?${params}`,
    );
  }

  /**
   * Updates stock hold statuses for multiple vehicles.
   * @param payload The request body containing the list of vehicles and their updated statuses.
   * @returns An Observable with the API response.
   */
  updateAllStockHolds(payload: {
    modelList: { Name: string; Value: string }[];
  }): Observable<any> {
    return this.http.post(
      `${this.stockHoldBaseUrl}/UpdateAll`,
      payload,
    );
  }

  /**
   * Fetches list of vehicles.
   * @param payload Colour and model to search
   * @returns An Observable with the API response.
   */
  getVehicleList(colour: string, model: string): Observable<any> {
    const encodedColour = encodeURIComponent(JSON.stringify(colour));
    const encodedModel = encodeURIComponent(JSON.stringify(model));

    const params = `colour=${encodedColour}&model=${encodedModel}`;

    return this.http.get(
      `${this.stockHoldBaseUrl}/GetVehicleList?${params}`,
    );
  }
}
